import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { 
  Package,
  QrCode,
  Truck,
  ArrowLeft,
  Box,
  Calendar
} from 'lucide-react';
import { ProjectCarton, CartonStatus, CartonReceivingStatus, Project } from '../types';
import { apiService } from '../services/api';
import { useAuth, useProject } from '../contexts';
import Scanner from '../components/Scanner';
import Button from '../components/Button';
import { Card } from '../components/Card';
import { TextArea } from '../components/Input';
import { StatusBadge } from '../components/StatusBadge';
import { cacheService } from '../services/cacheService';
import { cartonCacheService } from '../services/cartonCacheService';
import { ProjectSelector } from '../components/ProjectSelector';
import { toast } from 'react-hot-toast';

const DispatchContainer = styled.div`
  min-height: 100vh;
  background: ${({ theme }) => theme?.colors?.background || '#f5f5f5'};
  padding: ${({ theme }) => theme?.spacing?.[6] || '1.5rem'};
`;

const Header = styled.div`
  margin-bottom: ${({ theme }) => theme?.spacing?.[6] || '1.5rem'};
`;

const HeaderTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme?.spacing?.[4] || '1rem'};
  
  @media (max-width: ${({ theme }) => theme?.breakpoints?.md || '768px'}) {
    flex-direction: column;
    gap: ${({ theme }) => theme?.spacing?.[4] || '1rem'};
    align-items: stretch;
  }
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme?.spacing?.[2] || '0.5rem'};
  padding: ${({ theme }) => theme?.spacing?.[2] || '0.5rem'} ${({ theme }) => theme?.spacing?.[3] || '0.75rem'};
  background: transparent;
  border: 1px solid ${({ theme }) => theme?.colors?.border || '#ddd'};
  border-radius: ${({ theme }) => theme?.borderRadius?.lg || '0.5rem'};
  color: ${({ theme }) => theme?.colors?.textSecondary || '#666'};
  cursor: pointer;
  transition: ${({ theme }) => theme?.transition?.fast || '0.2s ease'};
  margin-bottom: ${({ theme }) => theme?.spacing?.[4] || '1rem'};

  &:hover {
    background: ${({ theme }) => theme?.colors?.gray50 || '#f5f5f5'};
    border-color: ${({ theme }) => theme?.colors?.primary || '#007bff'};
    color: ${({ theme }) => theme?.colors?.primary || '#007bff'};
  }
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme?.typography?.['3xl'] || '1.875rem'};
  font-weight: ${({ theme }) => theme?.typography?.bold || 700};
  color: ${({ theme }) => theme?.colors?.text || '#333'};
  margin: 0;
`;

const Subtitle = styled.p`
  font-size: ${({ theme }) => theme?.typography?.base || '1rem'};
  color: ${({ theme }) => theme?.colors?.textSecondary || '#666'};
  margin: ${({ theme }) => theme?.spacing?.[2] || '0.5rem'} 0 0 0;
`;

const Actions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme?.spacing?.[3] || '0.75rem'};
  
  @media (max-width: ${({ theme }) => theme?.breakpoints?.sm || '576px'}) {
    flex-direction: column;
  }
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme?.spacing?.[6] || '1.5rem'};

  @media (max-width: ${({ theme }) => theme?.breakpoints?.lg || '992px'}) {
    grid-template-columns: 1fr;
  }
`;

const ScanSection = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme?.spacing?.[8] || '2rem'} ${({ theme }) => theme?.spacing?.[4] || '1rem'};
  border: 2px dashed ${({ theme }) => theme?.colors?.border || '#ddd'};
  border-radius: ${({ theme }) => theme?.borderRadius?.xl || '1rem'};
  margin-bottom: ${({ theme }) => theme?.spacing?.[6] || '1.5rem'};
`;

const ScanIcon = styled.div`
  width: 64px;
  height: 64px;
  margin: 0 auto ${({ theme }) => theme?.spacing?.[4] || '1rem'};
  background: ${({ theme }) => theme?.colors?.primaryBackground || '#007bff'};
  border-radius: ${({ theme }) => theme?.borderRadius?.full || '50%'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme?.colors?.primary || '#007bff'};
`;

const ScanTitle = styled.h3`
  font-size: ${({ theme }) => theme?.typography?.lg || '1.125rem'};
  font-weight: ${({ theme }) => theme?.typography?.medium || 500};
  color: ${({ theme }) => theme?.colors?.text || '#333'};
  margin: 0 0 ${({ theme }) => theme?.spacing?.[2] || '0.5rem'} 0;
`;

const ScanDescription = styled.p`
  font-size: ${({ theme }) => theme?.typography?.sm || '0.875rem'};
  color: ${({ theme }) => theme?.colors?.textSecondary || '#666'};
  margin: 0 0 ${({ theme }) => theme?.spacing?.[6] || '1.5rem'} 0;
`;

const CartonDetails = styled.div`
  padding: ${({ theme }) => theme?.spacing?.[6] || '1.5rem'};
  background: ${({ theme }) => theme?.colors?.gray50 || '#f5f5f5'};
  border-radius: ${({ theme }) => theme?.borderRadius?.lg || '0.5rem'};
  margin-bottom: ${({ theme }) => theme?.spacing?.[6] || '1.5rem'};
`;

const CartonCode = styled.h3`
  font-size: ${({ theme }) => theme?.typography?.xl || '1.25rem'};
  font-weight: ${({ theme }) => theme?.typography?.semibold || 600};
  color: ${({ theme }) => theme?.colors?.text || '#333'};
  margin: 0 0 ${({ theme }) => theme?.spacing?.[4] || '1rem'} 0;
`;

const DetailsList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${({ theme }) => theme?.spacing?.[4] || '1rem'};
`;

const DetailItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme?.spacing?.[2] || '0.5rem'};
  font-size: ${({ theme }) => theme?.typography?.sm || '0.875rem'};
  color: ${({ theme }) => theme?.colors?.textSecondary || '#666'};
`;

const DispatchForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme?.spacing?.[4] || '1rem'};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme?.spacing?.[2] || '0.5rem'};
`;

const Label = styled.label`
  font-size: ${({ theme }) => theme?.typography?.sm || '0.875rem'};
  font-weight: ${({ theme }) => theme?.typography?.medium || 500};
  color: ${({ theme }) => theme?.colors?.text || '#333'};
`;

const DispatchActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme?.spacing?.[3] || '0.75rem'};
  justify-content: flex-end;
  margin-top: ${({ theme }) => theme?.spacing?.[4] || '1rem'};
`;

const ProjectBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: ${({ theme }) => theme?.colors?.primaryLight || '#add8e6'};
  color: ${({ theme }) => theme?.colors?.primary || '#007bff'};
  border-radius: 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  margin-top: 1rem;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme?.colors?.primary || '#007bff'};
    color: white;
  }
`;

const ProjectInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.875rem;
  color: ${({ theme }) => theme?.colors?.textSecondary || '#666'};
  margin-top: 4px;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  text-align: center;
  color: ${({ theme }) => theme?.colors?.textSecondary || '#666'};
  
  h3 {
    margin: 1rem 0 0.5rem;
    color: ${({ theme }) => theme?.colors?.text || '#333'};
  }
  
  p {
    margin-bottom: 1.5rem;
  }
`;

const ProjectHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme?.spacing?.[4] || '1rem'};
  background: ${({ theme }) => theme?.colors?.white || '#fff'};
  padding: ${({ theme }) => theme?.spacing?.[4] || '1rem'};
  border-radius: ${({ theme }) => theme?.borderRadius?.xl || '1rem'};
  box-shadow: ${({ theme }) => theme?.shadows?.sm || '0 1px 3px rgba(0,0,0,0.1)'};
`;

const ProjectTitle = styled.h2`
  font-size: ${({ theme }) => theme?.typography?.xl || '1.25rem'};
  font-weight: ${({ theme }) => theme?.typography?.bold || 700};
  color: ${({ theme }) => theme?.colors?.text || '#333'};
  margin: 0;
`;

const ProjectSubtitle = styled.p`
  font-size: ${({ theme }) => theme?.typography?.sm || '0.875rem'};
  color: ${({ theme }) => theme?.colors?.textSecondary || '#666'};
  margin: ${({ theme }) => theme?.spacing?.[1] || '0.25rem'} 0 0 0;
`;

const SwitchButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme?.colors?.primary || '#007bff'};
  font-weight: ${({ theme }) => theme?.typography?.medium || 500};
  cursor: pointer;
  padding: ${({ theme }) => theme?.spacing?.[2] || '0.5rem'} ${({ theme }) => theme?.spacing?.[3] || '0.75rem'};
  border-radius: ${({ theme }) => theme?.borderRadius?.md || '0.375rem'};
  transition: all 0.2s ease;
  
  &:hover {
    background: ${({ theme }) => theme?.colors?.primaryBackground || '#add8e6'};
  }
`;

const ProjectStats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${({ theme }) => theme?.spacing?.[4] || '1rem'};
  margin-bottom: ${({ theme }) => theme?.spacing?.[6] || '1.5rem'};
`;

const StatCard = styled.div`
  background: ${({ theme }) => theme?.colors?.white || '#fff'};
  border: 1px solid ${({ theme }) => theme?.colors?.border || '#ddd'};
  border-radius: ${({ theme }) => theme?.borderRadius?.xl || '1rem'};
  padding: ${({ theme }) => theme?.spacing?.[4] || '1rem'};
  box-shadow: ${({ theme }) => theme?.shadows?.sm || '0 1px 3px rgba(0,0,0,0.1)'};
`;

const StatHeader = styled.div`
  font-size: ${({ theme }) => theme?.typography?.sm || '0.875rem'};
  color: ${({ theme }) => theme?.colors?.textSecondary || '#666'};
  margin-bottom: ${({ theme }) => theme?.spacing?.[2] || '0.5rem'};
`;

const StatValue = styled.div`
  font-size: ${({ theme }) => theme?.typography?.['2xl'] || '1.5rem'};
  font-weight: ${({ theme }) => theme?.typography?.bold || 700};
  color: ${({ theme }) => theme?.colors?.primary || '#007bff'};
`;

const StatBreakdown = styled.div`
  display: flex;
  gap: ${({ theme }) => theme?.spacing?.[3] || '0.75rem'};
  margin-top: ${({ theme }) => theme?.spacing?.[2] || '0.5rem'};
  font-size: ${({ theme }) => theme?.typography?.sm || '0.875rem'};
  color: ${({ theme }) => theme?.colors?.textSecondary || '#666'};
`;

const CartonGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: ${({ theme }) => theme?.spacing?.[4] || '1rem'};
  margin-top: ${({ theme }) => theme?.spacing?.[6] || '1.5rem'};
`;

const SectionTitle = styled.h3`
  font-size: ${({ theme }) => theme?.typography?.lg || '1.125rem'};
  font-weight: ${({ theme }) => theme?.typography?.semibold || 600};
  color: ${({ theme }) => theme?.colors?.text || '#333'};
  margin: ${({ theme }) => theme?.spacing?.[6] || '1.5rem'} 0 ${({ theme }) => theme?.spacing?.[3] || '0.75rem'} 0;
`;

const CartonDispatchPage: React.FC = () => {
  const [selectedCarton, setSelectedCarton] = useState<CartonReceivingStatus | null>(null);
  const [remarks, setRemarks] = useState('');
  const [loading, setLoading] = useState(false);
  const [dispatching, setDispatching] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [projectData, setProjectData] = useState<any>(null);
  const [showProjectSelector, setShowProjectSelector] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [scannedCartons, setScannedCartons] = useState<ProjectCarton[]>([]);
  
  const navigate = useNavigate();
  const { user } = useAuth();
  const { selectedProject, setSelectedProject } = useProject();

  // Add debug logging for project and scanner state
  useEffect(() => {
    console.log('CartonDispatchPage project state:', { 
      hasSelectedProject: !!selectedProject,
      selectedProject: selectedProject ? { 
        id: selectedProject.id, 
        name: selectedProject.name 
      } : null,
      showScanner,
      projectData: projectData ? { 
        cartons: projectData.cartons?.length,
        status: projectData.status 
      } : null
    });
  }, [selectedProject, showScanner, projectData]);

  // Load projects
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const projectsData = await apiService.getProjects();
        setProjects(projectsData);
      } catch (error) {
        console.error('Error fetching projects:', error);
        toast.error('Failed to load projects');
      }
    };

    fetchProjects();
  }, []);

  // Load project data when selected - optimized like CartonContentReceivingPage
  useEffect(() => {
    const loadProjectData = async () => {
      if (!selectedProject) {
        setProjectData(null);
        setScannedCartons([]);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        // Use the same efficient pattern as CartonContentReceivingPage
        const projectData = await apiService.getProject(selectedProject.id);
        
        if (projectData) {
          console.log('CartonDispatchPage: Project data loaded:', projectData);
          setProjectData(projectData);
          
          // Extract cartons from project data if available
          if (projectData.cartons && Array.isArray(projectData.cartons)) {
            setScannedCartons(projectData.cartons);
            console.log('CartonDispatchPage: Cartons loaded from project data:', projectData.cartons.length);
          } else {
            // Fallback: Load cartons separately if not in project data
            try {
              const cartonsData = await apiService.getProjectCartons(selectedProject.id);
              setScannedCartons(cartonsData);
              console.log('CartonDispatchPage: Cartons loaded separately:', cartonsData.length);
            } catch (cartonsError) {
              console.warn('CartonDispatchPage: Failed to load cartons separately:', cartonsError);
              setScannedCartons([]);
            }
          }
        } else {
          throw new Error('Invalid project data received');
        }
      } catch (error) {
        console.error('CartonDispatchPage: Error loading project data:', error);
        setError('Failed to load project data. Please try again.');
        toast.error('Failed to load project data');
      } finally {
        setLoading(false);
      }
    };

    loadProjectData();
  }, [selectedProject]);

  const handleScan = async (barcode: string) => {
    if (!selectedProject) {
      toast.error('Please select a project first');
      setShowProjectSelector(true);
      return;
    }

    try {
      setLoading(true);
      
      // First try to find in the current project data
      let carton = projectData?.cartons?.find((c: any) => c.code === barcode || c.id === barcode);
      
      // If not found, try the carton cache
      if (!carton) {
        carton = await cartonCacheService.getCartonDetails(barcode);
      }
      
      // If still not found, try the API
      if (!carton) {
        carton = await apiService.scanBarcode(barcode, selectedProject.id);
        if (carton) {
          await cartonCacheService.cacheCartonDetails(carton);
        }
      }

      if (carton) {
        // Check if already scanned
        if (scannedCartons.some(c => c.id === carton.id)) {
          toast.error('This carton has already been scanned');
          return;
        }
        
        setScannedCartons(prev => [...prev, carton]);
        toast.success('Carton scanned successfully');
      } else {
        toast.error('Carton not found in project');
      }
      
      setShowScanner(false);
    } catch (error) {
      console.error('Error scanning barcode:', error);
      toast.error('Failed to scan carton: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleProjectSelect = (project: Project) => {
    setSelectedProject(project);
    setShowProjectSelector(false);
  };

  const handleDispatchCarton = async () => {
    if (!selectedCarton || !user || !selectedProject) return;

    setDispatching(true);
    try {
      await apiService.updateCartonStatus(
        selectedCarton.carton.id, 
        'dispatched', 
        remarks,
        selectedProject.id
      );
      toast.success('Carton dispatched successfully');
      setSelectedCarton(null);
      setRemarks('');
    } catch (error) {
      console.error('Error dispatching carton:', error);
      toast.error('Failed to dispatch carton');
    } finally {
      setDispatching(false);
    }
  };

  return (
    <DispatchContainer>
      <Header>
        <BackButton onClick={() => navigate(-1)}>
          <ArrowLeft size={20} />
          Back
        </BackButton>
        <Title>Dispatch Cartons</Title>
        <Subtitle>Scan and dispatch cartons to the site</Subtitle>
      </Header>

      {showProjectSelector && (
        <ProjectSelector
          projects={projects}
          onSelect={handleProjectSelect}
          onClose={() => setShowProjectSelector(false)}
        />
      )}

      {selectedProject ? (
        <>
          <ProjectHeader>
            <div>
              <ProjectTitle>
                {projectData?.project?.display_name || selectedProject.name}
              </ProjectTitle>
              <ProjectSubtitle>
                {projectData?.project?.customer_name || 'Loading project details...'}
                {projectData?.project?.so_number && ` • SO: ${projectData.project.so_number}`}
              </ProjectSubtitle>
            </div>
            <SwitchButton onClick={() => setShowProjectSelector(true)}>
              Switch Project
            </SwitchButton>
          </ProjectHeader>

          {loading ? (
            <EmptyState>
              Loading project data...
            </EmptyState>
          ) : projectData ? (
            <>
              <ProjectStats>
                <StatCard>
                  <StatHeader>Total Cartons</StatHeader>
                  <StatValue>{projectData.cartons?.length || 0}</StatValue>
                </StatCard>
                <StatCard>
                  <StatHeader>Ready to Dispatch</StatHeader>
                  <StatValue>
                    {projectData.cartons?.filter(c => c.status === 'received').length || 0}
                  </StatValue>
                  <StatBreakdown>
                    <span>Total: {projectData.cartons?.length || 0}</span>
                  </StatBreakdown>
                </StatCard>
                <StatCard>
                  <StatHeader>Dispatched</StatHeader>
                  <StatValue>
                    {projectData.cartons?.filter(c => c.status === 'dispatched').length || 0}
                  </StatValue>
                </StatCard>
                <StatCard>
                  <StatHeader>Total Panels</StatHeader>
                  <StatValue>
                    {projectData?.statistics?.panels?.total_panels || 
                     (projectData?.panels?.length || 0) || 
                     (projectData.cartons || []).reduce((sum, c) => sum + (c.panel_items || c.panels?.length || 0), 0)}
                  </StatValue>
                </StatCard>
                <StatCard>
                  <StatHeader>Total Hardware</StatHeader>
                  <StatValue>
                    {projectData?.statistics?.hardware?.total_hardware || 
                     (projectData?.hardware?.length || 0) || 
                     (projectData.cartons || []).reduce((sum, c) => sum + (c.hw_items || c.hardware?.length || 0), 0)}
                  </StatValue>
                </StatCard>
              </ProjectStats>

              <ContentGrid>
                <div>
                  <ScanSection>
                    <ScanIcon>
                      <QrCode size={32} />
                    </ScanIcon>
                    <ScanTitle>Scan Carton</ScanTitle>
                    <ScanDescription>
                      Use the scanner to scan the carton QR code
                    </ScanDescription>
                    <Button
                      onClick={() => setShowScanner(true)}
                      disabled={loading}
                    >
                      <QrCode size={20} />
                      Start Scanning
                    </Button>
                  </ScanSection>

                  {selectedCarton && (
                    <CartonDetails>
                      <CartonCode>{selectedCarton.carton.code}</CartonCode>
                      <DetailsList>
                        <DetailItem>
                          <Box size={16} />
                          <span>Status: <StatusBadge status={selectedCarton.carton.status as CartonStatus}>
                            {selectedCarton.carton.status}
                          </StatusBadge></span>
                        </DetailItem>
                        <DetailItem>
                          <Calendar size={16} />
                          <span>Last Updated: {new Date(selectedCarton.carton.received_at || '').toLocaleString()}</span>
                        </DetailItem>
                      </DetailsList>
                      
                      <DispatchForm>
                        <FormGroup>
                          <Label>Remarks (Optional)</Label>
                          <TextArea
                            placeholder="Add any remarks about the dispatch..."
                            value={remarks}
                            onChange={(e) => setRemarks(e.target.value)}
                            rows={3}
                          />
                        </FormGroup>

                        <Button 
                          variant="primary"
                          onClick={handleDispatchCarton}
                          disabled={dispatching}
                          fullWidth
                        >
                          <Truck size={18} />
                          {dispatching ? 'Dispatching...' : 'Dispatch Carton'}
                        </Button>
                      </DispatchForm>
                    </CartonDetails>
                  )}
                </div>

                
                <div>
                  <SectionTitle>Cartons Ready for Dispatch</SectionTitle>
                  <CartonGrid>
                    {projectData.cartons
                      ?.filter(c => c.status === 'received')
                      .map((carton) => (
                        <Card key={carton.id}>
                          <CartonCode>{carton.code}</CartonCode>
                          <DetailsList>
                            <DetailItem>
                              <Box size={16} />
                              <span>{carton.panels?.length || 0} panels</span>
                            </DetailItem>
                            <DetailItem>
                              <Box size={16} />
                              <span>{carton.hardware?.length || 0} hardware items</span>
                            </DetailItem>
                            {carton.received_at && (
                              <DetailItem>
                                <Calendar size={16} />
                                <span>Received {new Date(carton.received_at).toLocaleDateString()}</span>
                              </DetailItem>
                            )}
                          </DetailsList>
                        </Card>
                      ))}
                  </CartonGrid>

                  <SectionTitle style={{ marginTop: '2rem' }}>Recently Dispatched</SectionTitle>
                  <CartonGrid>
                    {projectData.cartons
                      ?.filter(c => c.status === 'dispatched')
                      .slice(0, 3) // Show only 3 most recent
                      .map((carton) => (
                        <Card key={carton.id}>
                          <CartonCode>{carton.code}</CartonCode>
                          <DetailsList>
                            <DetailItem>
                              <Box size={16} />
                              <span>{carton.panels?.length || 0} panels</span>
                            </DetailItem>
                            <DetailItem>
                              <Box size={16} />
                              <span>{carton.hardware?.length || 0} hardware items</span>
                            </DetailItem>
                            {carton.dispatched_at && (
                              <DetailItem>
                                <Calendar size={16} />
                                <span>Dispatched {new Date(carton.dispatched_at).toLocaleDateString()}</span>
                              </DetailItem>
                            )}
                          </DetailsList>
                        </Card>
                      ))}
                  </CartonGrid>
                </div>
              </ContentGrid>
            </>
          ) : (
            <EmptyState>
              Failed to load project data. Please try again.
            </EmptyState>
          )}
        </>
      ) : (
        <EmptyState>
          <Package size={48} />
          <h3>No Project Selected</h3>
          <p>Please select a project to dispatch cartons</p>
          <Button onClick={() => setShowProjectSelector(true)}>
            Select Project
          </Button>
        </EmptyState>
      )}

      {showScanner && (
        <Scanner
          onClose={() => setShowScanner(false)}
          onScan={handleScan}
          title="Scan Carton Barcode"
        />
      )}
    </DispatchContainer>
  );
};

export default CartonDispatchPage;